import { environment } from "../../../environments/environment";
import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AccountInfoExtended } from "@ags/core";
import {
  BillingMeterDetailsPayload,
  ProductOnboardBigIdPayload,
  ProductOnboardStatusPayload,
  Region,
  Service,
} from "./model";
import { Product } from "../ondemand/model";
import * as _ from "lodash";
import { Status } from "../status/model";
import { SkipList } from "../skip-list/model";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private backendApiRoot = environment.betApiEndpoint;

  constructor(private http: HttpClient) {}

  private send<T>(block: (HttpClient) => Observable<T>) {
    const handleError = this.handleError;
    return block(this.http).pipe(catchError(handleError));
  }

  private get<T>(url: string, options = {}): Observable<T> {
    return this.send((http) => http.get(`${this.backendApiRoot}${url}`, options));
  }

  private post<T>(url: string, body = {}, options = {}): Observable<T> {
    return this.send((http) => http.post(`${this.backendApiRoot}${url}`, body, options));
  }

  CheckPermission() {
    return this.get("/api/checkPermission");
  }

  GetClouds() {
    return this.get("/api/Clouds");
  }

  GetRegions() {
    return this.get("/api/Regions");
  }

  GetEdgeZones() {
    return this.get("/api/EdgeZones");
  }

  GetPsl(regionName: string) {
    return this.get(`/api/PSL/${regionName}`);
  }

  getSkipList() {
    return this.get(`/api/SkipList`);
  }

  modifySkipList(modifiedSkipList: SkipList[]) {
    return this.post(`/api/SkipList`, modifiedSkipList);
  }
  downloadFullReport(): any {
    return this.get(`/api/productRequestReport`, { responseType: "blob" });
  }

  exportExcelWithDetails(requests: any[]): any {
    return this.post(`/api/ExportBillingMeterDetails`, { data: requests }, { responseType: "blob" });
  }

  GetPslProduct(pslId: number) {
    return this.get(`/api/PSL/product/${pslId}`);
  }

  SubmitPslProduct(pslId: number, caymanProductIds: string[]) {
    return this.post(`/api/PSL/product/${pslId}`, caymanProductIds);
  }

  GetProductOnboardStatus(): Observable<ProductOnboardStatusPayload> {
    return this.get<ProductOnboardStatusPayload>(`/api/GetOnboarding`);
  }

  GetProductOnboardingBigIds(bigId: string): Observable<ProductOnboardBigIdPayload> {
    return this.get<ProductOnboardBigIdPayload>(`/api/GetOnboarding/BigIds/${bigId}`);
  }

  GetRequestStatus(regionNames: string[], key: string, fromDate: string, toDate: string) {
    return this.get(`/api/RequestStatus/${regionNames.join(",")}/'${key}'/'${fromDate}'/'${toDate}'`);
  }

  GetRequestStatusByJobId(jobId: string, fromDate: string, toDate: string) {
    return this.get(`/api/RequestStatus/''/''/'${fromDate}'/'${toDate}'?job_id=${jobId}`);
  }

  GetBillingMeterDetails(jobId: string, bigId: string): Observable<BillingMeterDetailsPayload> {
    return this.get<BillingMeterDetailsPayload>(`/api/BillingMeterDetails/${jobId}/${bigId}`);
  }

  GetProductList(RegionNames: string[], product: string) {
    const newProduct = product.replace(/[,|\n|\n\r]+/g, ",");
    return this.post("/api/OndemandProduct/", {
      regions: RegionNames,
      key: newProduct,
    });
  }

  GetProductTrackInfo(key: string, region: string) {
    return this.get(`/api/ProductStatusTrack?key=${key}&region=${region}`);
  }

  PostOnDemandPreBuildout(products: Product[], user: AccountInfoExtended) {
    const caymanProducts = _.map(
      _.groupBy(products, (p) => p.RegionName),
      (v, k) => {
        return {
          regionName: k,
          caymanProducts: v,
          createdBy: user.username,
        };
      }
    );
    return this.post(`/api/OnDemandPrebuildout`, caymanProducts);
  }

  private handleError = (error: HttpErrorResponse) => {
    let errorMessage = "";
    if (error.error instanceof ErrorEvent) {
      errorMessage = `A client-side or network error occurred: ${error.error.message}`;
    } else {
      if (error.status === 0) {
        errorMessage = "AJAX request was cancelled. ";
      } else if (error.status === 404) {
        return this.toError(error);
      } else if (error.status === 403) {
        return this.toError(error);
      }

      if (error.error?.message) {
        errorMessage += `Server response: ${error.error.message}.`;
      } else if (typeof error.error === "string") {
        errorMessage += `Server response: ${error.error}.`;
      } else {
        // format error message (exclude http statusText since it's removed in http2)
        // refer to source code https://github.com/angular/angular/blob/main/packages/common/http/src/response.ts#L319-L350
        if (error.status >= 200 && error.status < 300) {
          errorMessage += `Server response: Http failure during parsing for ${error.url || "(unknown url)"}.`;
        } else {
          errorMessage += `Server response: Http failure response for ${error.url || "(unknown url)"}: ${error.status}.`;
        }
      }
    }

    console.error(errorMessage);
    return throwError({ message: errorMessage });
  };

  private toError(error: HttpErrorResponse) {
    const errorMessage = error.status === 404 ? "Resource not found" :
                        error.status === 403 ? "Access denied" : error.message;
    console.error(`Error ${error.status}: ${errorMessage}`);
    return throwError({ message: errorMessage });
  }
}
